import { useState } from "react";
import cx from 'classnames';
import Button from "../common/Button";
import { FaExternalLinkAlt, FaList, FaShoppingCart, FaTrash, FaUnlink, FaPlus, FaSync } from "react-icons/fa";
import { SearchSelect } from "../common/SearchSelect/SearchSelect";
import { postMessageToLeboncoinExt } from "../../lib/leboncoin/LeboncoinExtApi";
import { formatCurrency } from "../../utils/currency";
import { OrderPage_AdVariationFragment, OrderPage_GetExistingOrderQuery, OrderPageLineItems_SyncAliexpressSupplierProductMutation, OrderPageLineItems_SyncAliexpressSupplierProductMutationVariables } from "../../../codegen/graphql";
import { gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import AddLineItemModal from "./AddLineItemModal";
import { DeliveryDestination } from "../../../common/deliveryDestinations";
import { useRequestManager } from "../common/RequestManager";
import SupplierVariation_SearchSelect, { SupplierVariationSearchSelect_SupplierVariationOption } from "../common/SearchSelect/SupplierVariation_SearchSelect";
import RestockVariation_SearchSelect, { RestockVariationSearchSelect_RestockVariationOption } from "../common/SearchSelect/RestockVariation_SearchSelect";
import { getTranslatedValue, Translations } from "../common/TranslatedValue";

export function OrderPageLineItems({
  orderData,
  addressForm,
  onRefresh,
}: {
  orderData: NonNullable<OrderPage_GetExistingOrderQuery['order']>
  addressForm: any;
  onRefresh?: () => void
}) {
  const { trackRequest } = useRequestManager();
  const deliveryDestination = orderData.customerOriginalAddressCountryCode || 'FR';
  const translationLang = orderData.customerOriginalAddressCountryCode;
  
  // State for adding new order product variations
  const [showAddLineItemModal, setShowAddLineItemModal] = useState(false);

  // Mutations
  const [updateOrderProductsVariation] = useMutation(UPDATE_ORDER_PRODUCTS_VARIATION);
  const [deleteOrderProductsVariation] = useMutation(DELETE_ORDER_PRODUCTS_VARIATION);

  const [syncAliexpressSupplierProduct] = useMutation<
    OrderPageLineItems_SyncAliexpressSupplierProductMutation,
    OrderPageLineItems_SyncAliexpressSupplierProductMutationVariables
  >(ORDER_PAGE_LINE_ITEMS_SYNC_ALIEXPRESS_SUPPLIER_PRODUCT);

  // Handle supplier product variation selection change
  const handleSupplierProductVariationChange = async (
    orderLineItemId: string,
    selectedSupplierVariation: SupplierVariationSearchSelect_SupplierVariationOption | null,
  ) => {
    if (!selectedSupplierVariation) {
      return;
    }

    await trackRequest(
      updateOrderProductsVariation({
        variables: {
          id: orderLineItemId,
          input: {
            supplierProductId: selectedSupplierVariation.supplierProductId,
            supplierSku: selectedSupplierVariation.sku,
          },
        },
      }), {
      title: 'Updating supplier variation',
      isBlocking: false,
    });
  };

  // Handle restock shipment product variation selection change
  const handleRestockVariationChange = async (orderLineItemId: string, selectedRestockVariation: RestockVariationSearchSelect_RestockVariationOption | null) => {
    if (!selectedRestockVariation) {
      return;
    }

    await trackRequest(
      updateOrderProductsVariation({
        variables: {
          id: orderLineItemId,
          input: {
            restockShipmentProductVariationId: selectedRestockVariation.id,
          },
        },
        }
      ), {
      title: 'Updating restock variation',
      isBlocking: false,
    });
  };

  // Handle removing restock shipment variation association
  const handleRemoveRestockShipmentVariation = async (orderLineItemId: string) => {
    await trackRequest(
      updateOrderProductsVariation({
        variables: {
          id: orderLineItemId,
          input: {
            restockShipmentProductVariationId: null,
          },
        },
      }
    ), {
      title: 'Removing restock shipment variation',
    });
  };

  // Handle deleting order product variation
  const handleDeleteOrderProductVariation = async (orderLineItemId: string) => {
    if (window.confirm('Are you sure you want to delete this order product variation? This action cannot be undone.')) {
      try {
        await deleteOrderProductsVariation({
          variables: {
            id: orderLineItemId,
          },
        });
      } catch (error) {
        console.error('Error deleting order product variation:', error);
      }
    }
  };

  // Function to handle type value selection change
  const handleTypeValueChange = async (
    orderProductsVariation: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderProductsVariations'][0],
    newTypeId: string,
    newTypeValueId: string,
  ) => {
    const currentVariation = orderProductsVariation.adVariation;
    const selectedVariation = orderProductsVariation.adVariation?.ad.variations.find(
      (variation) => {
        // Find variation with all type values
        const toFind = currentVariation?.variationTypeValues.reduce<string[]>(
          (acc, variationTypeValue) => {
            if (variationTypeValue.type.id === newTypeId) {
              return acc;
            }

            acc.push(`${variationTypeValue.type.id}-${variationTypeValue.id}`);
            return acc;
          },
          []
        ) || [];

        toFind.push(`${newTypeId}-${newTypeValueId}`);

        for (const variationTypeValue of variation.variationTypeValues) {
          const toFindIndex = toFind.indexOf(`${variationTypeValue.type.id}-${variationTypeValue.id}`);
          if (toFindIndex !== -1) {
            toFind.splice(toFindIndex, 1);
          }
        }

        return toFind.length === 0;
      }
    );

    if (!selectedVariation) {
      toast.error('Selected variation not found');
      return;
    }

    await updateOrderProductsVariation({
      variables: {
        id: orderProductsVariation.id,
        input: {
          variationId: selectedVariation.id,
          supplierProductId: null,
          supplierSku: null,
        },
      },
    });
  };

  const getVariationImages = (variationData: OrderPage_AdVariationFragment | undefined | null) => {
    if (!variationData) {
      return [];
    }

    if (!variationData.ad.typeIdUsedForImages) {
      return []
    } 

    const typeValue = variationData.variationTypeValues.find(
      (variationTypeValue) => variationTypeValue.type.id === variationData.ad.typeIdUsedForImages
    );

    return typeValue?.medias || [];
  };

  // Function to identify missing line items
  const getMissingLineItems = () => {
    if (!orderData.originalLineItems || !orderData.orderProductsVariations) {
      return [];
    }

    // Get all external line item IDs that have order product variations
    const existingLineItemIds = new Set(
      orderData.orderProductsVariations
        .map(opv => opv.externalLineItemId)
        .filter(Boolean)
    );

    // Find original line items that don't have corresponding order product variations
    return orderData.originalLineItems.filter(lineItem => 
      !existingLineItemIds.has(lineItem.lineItemId)
    );
  };

  // Function to close modal and reset pre-filled data
  const handleCloseModal = () => {
    setShowAddLineItemModal(false);
  };

  // Function to handle modal success
  const handleModalSuccess = () => {
    setShowAddLineItemModal(false);
    onRefresh?.();
  };

  // Function to handle resyncing Aliexpress suppliers
  const handleResyncAliexpressSuppliers = async (
    orderProductsVariation: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderProductsVariations'][number]
  ) => {
    const allProductsSync = Promise.all(
      (orderProductsVariation.adVariation?.supplierProductVariations || []).map(async (supplierProductVariation) => {
        return syncAliexpressSupplierProduct({
          variables: {
            productId: supplierProductVariation.supplierProduct.externalProductId!,
            countries: [deliveryDestination],
            options: {
              onlyForVariations: [supplierProductVariation.sku],
              forceRefresh: true,
            },
          },
        });
      })
    );

    await trackRequest(
      allProductsSync, {
        title: 'Re-synchronizing Aliexpress suppliers',
        isBlocking: false,
      }
    );
  };

  const missingLineItems = getMissingLineItems();

  return (<>
    <div className="flex flex-row gap-2 mb-2">
      <Button
        preset="white"
        size="md"
        onClick={() => setShowAddLineItemModal(true)}
      >
        <FaPlus className="mr-2" />
        Add Line Item
      </Button>
    </div>
    <div className="bg-white border-1 border-gray-300 mb-2">
      <div className="grid grid-cols-16">
        <div className="col-span-2 px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
          Image
        </div>
        <div className="col-span-6 px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
          Title
        </div>
        <div className="col-span-4 px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
          Fullfill variations
        </div>
        <div className="col-span-2 px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
          Quantity
        </div>
        <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-gray-500 uppercase tracking-wider">
          Total Cost
        </div>
      </div>
    </div>

    
    {/* Display existing order product variations */}
    {orderData.orderProductsVariations.length > 0
    && orderData.orderProductsVariations.map((orderProductsVariation, index: number) => {
      return (
        <div
          key={orderProductsVariation.adVariation?.id || index}
        >
          <div className="flex items-center flex-start mb-2 mt-2">
            <h3 className="text-lg font-medium text-gray-900">
              Line item #{orderProductsVariation.externalLineItemId}
            </h3>

            <div className="flex gap-2 ml-auto">
              <Button
                preset="white"
                size="sm"
                onClick={() => handleDeleteOrderProductVariation(orderProductsVariation.id)}
                className="text-red-600 hover:text-red-700"
              >
                <FaTrash className="mr-1" size={14} />
                Delete
              </Button>
            </div>
          </div>
          <div
            className="grid grid-cols-16 border-1 border-gray-300 bg-white"
          >
            <div className="col-span-2 px-4 py-4 flex items-center">
              {getVariationImages(orderProductsVariation.adVariation).map((image) => (
                <img
                  //@ts-ignore
                  src={image.url} 
                  alt={getTranslatedValue(orderProductsVariation.adVariation?.ad.title as unknown as Translations, translationLang)} 
                  className="h-32 w-32 rounded-md object-cover"
                />
              ))}
            </div>
            
            <div className="col-span-6 px-4 py-4 flex flex-col justify-center items-start">
              {orderProductsVariation.adVariation?.id && (
                <div className="text-xs text-gray-500">SKU: {orderProductsVariation.adVariation?.id}</div>
              )}
              <div className="text-lg font-medium text-gray-900">{getTranslatedValue(orderProductsVariation.adVariation?.ad.title as unknown as Translations, translationLang)}</div>

              <Button
                preset="white"
                onClick={() => {
                  window.open(`/ad/${orderProductsVariation.adVariation?.ad.id}/edit/suppliers`, '_blank');
                }}
              >
                <FaList className="mr-1" size={16} />
                Open ad listing
              </Button>
            </div>
            
            <div className="col-span-4 px-4 py-4 flex flex-col justify-center">
              {orderProductsVariation.adVariation?.ad.variationTypes.map((variationType) => {
                const selectedTypeValue = orderProductsVariation.adVariation?.variationTypeValues.find(
                  vtv => vtv.type.id === variationType.id
                )!;

                return (
                  <div key={variationType.id} className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {getTranslatedValue(variationType.type as unknown as Translations, translationLang)}
                    </label>
                    <SearchSelect
                      loadOptions={() => Promise.resolve(variationType.typeValues)}
                      renderOption={(typeValue, selectCallback) => (
                        <TypeValueOption
                          typeValue={typeValue}
                          isSelected={false}
                          selectCallback={selectCallback}
                          lang={translationLang}
                        />
                      )}
                      renderSelected={(typeValue) => (
                        <TypeValueOption
                          typeValue={typeValue}
                          isSelected={true}
                          lang={translationLang}
                        />
                      )}
                      value={selectedTypeValue || null}
                      onSelect={(typeValue) => {
                        if (typeValue) {
                          handleTypeValueChange(orderProductsVariation, variationType.id, typeValue.id);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
            
            <div className="col-span-2 px-4 py-4 flex flex-col">
              x {orderProductsVariation.quantity} units
              <div className="text-lg text-gray-900 flex flex-row gap-2 flex-wrap">
                {Array.from({length: orderProductsVariation.quantity}).map((_, index) => (
                  <div
                    key={index}
                    className="text-lg text-gray-900 rounded-full bg-gray-200 w-10 h-10 flex items-center justify-center"

                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="col-span-2 px-4 py-4 text-right flex flex-col items-center justify-center">
                <span className="text-xl">{formatCurrency(orderProductsVariation.money.grossSale)}</span>
                
                <span className="text-lg text-green-700">
                  {<span
                    className={cx(orderProductsVariation.money.grossMargin > 0 ? 'text-green-700' : 'text-red-700')}
                  >
                    {formatCurrency(orderProductsVariation.money.grossMargin)}
                  </span>}
                </span>
            </div>
          </div>
          
          {/* Supplier Product Variation Section */}
          <div className="mb-8 grid grid-cols-16 bg-yellow-50 border-b border-l border-r border-gray-300">
            {(() => {

              // Get available supplier variations for this item
              const supplierVariations = orderProductsVariation.adVariation?.supplierProductVariations || [];

              // Get the currently selected supplier variation
              const selectedSupplierVariation = orderProductsVariation.supplierProductVariation;

              // Extract properties for display
              const lowestPriceSupplierVariation = supplierVariations.reduce((min: any, spv: any) => {
                return spv.cost < min.cost ? spv : min;
              }, supplierVariations[0]);
              
              return (
                <div className="col-span-14 px-4 py-4">
                  {/* Supplier Variation Selection Dropdown */}
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Supplier Variation
                      </label>

                      <div className="flex-1" />

                      <Button
                        preset="white"
                        size="sm"
                        onClick={() => handleResyncAliexpressSuppliers(orderProductsVariation)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <FaSync className="mr-1" size={16} />
                        Sync
                      </Button>
                    </div>
                    <SupplierVariation_SearchSelect
                      selectedSupplierVariation={selectedSupplierVariation}
                      onSupplierVariationChange={(selectedSupplierVariation) => {
                        handleSupplierProductVariationChange(orderProductsVariation.id, selectedSupplierVariation);
                      }}
                      deliveryDestination={deliveryDestination as DeliveryDestination}
                      linkedToAdVariationId={orderProductsVariation.adVariation?.id}
                    />
                  </div>
                  
                  {/* Restock Shipment Product Variation Selection Dropdown */}
                  {selectedSupplierVariation
                  && selectedSupplierVariation.supplierProduct.supplierName !== 'aliexpress'
                  && (
                    <div className="mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Select Restock Shipment Variation
                        </label>
                        {orderProductsVariation.restockShipmentProductVariation && (
                          <Button
                            preset="white"
                            size="sm"
                            onClick={() => handleRemoveRestockShipmentVariation(orderProductsVariation.id)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <FaUnlink className="mr-1" size={14} />
                            Remove
                          </Button>
                        )}
                      </div>
                      
                      <RestockVariation_SearchSelect
                        selectedRestockVariation={orderProductsVariation.restockShipmentProductVariation}
                        onRestockVariationChange={(selectedRestockVariation) => {
                          handleRestockVariationChange(orderProductsVariation.id, selectedRestockVariation);
                        }}
                        supplierProductId={selectedSupplierVariation.supplierProductId}
                        supplierVariationSku={selectedSupplierVariation.sku}
                      />
                    </div>
                  )}
                  
                  <div className="flex flex-row gap-2 justify-end">
                    <Button
                      preset="white"
                      disabled={!selectedSupplierVariation}
                      onClick={() => {
                        supplierVariations.forEach((supplierVariation) => {
                          window.open(`https://www.aliexpress.com/item/${supplierVariation.supplierProduct.externalProductId}.html`, '_blank');
                        });
                      }}
                    >
                      <FaExternalLinkAlt className="mr-2" size={20} />
                      Open all products
                    </Button>

                    <Button
                      preset="white"
                      disabled={!selectedSupplierVariation}
                      onClick={() => {
                        window.open(`https://www.aliexpress.com/item/${selectedSupplierVariation!.supplierProduct.externalProductId}.html`, '_blank');
                      }}
                    >
                      <FaExternalLinkAlt className="mr-2" size={20} />
                      Open product
                    </Button>

                    <Button
                      disabled={!selectedSupplierVariation}
                      onClick={() => {
                        const searchParams = new URLSearchParams();
                        searchParams.set('objectId', selectedSupplierVariation!.supplierProduct.externalProductId!);
                        searchParams.set('countryCode', 'FR');
                        searchParams.set('shippingCompany', selectedSupplierVariation!.data[deliveryDestination].freight.delivery_options.delivery_option_d_t_o[0].code);
                        searchParams.set('skuId', selectedSupplierVariation!.sku);
                        searchParams.set('quantity', orderProductsVariation.quantity.toString());
                        window.open(`https://www.aliexpress.com/p/trade/confirm.html?${searchParams.toString()}`, '_blank');
                      }}
                    >
                      <FaShoppingCart className="mr-2" size={20} />
                      Open cart
                    </Button>

                    <Button
                      disabled={!selectedSupplierVariation}
                      onClick={() => {
                        const searchParams = new URLSearchParams();
                        searchParams.set('objectId', selectedSupplierVariation!.supplierProduct.externalProductId!);
                        searchParams.set('countryCode', 'FR');
                        searchParams.set('countryCode', 'FR');
                        searchParams.set('shippingCompany', selectedSupplierVariation!.data[deliveryDestination].freight.delivery_options.delivery_option_d_t_o[0].code);
                        searchParams.set('skuId', selectedSupplierVariation!.sku);
                        searchParams.set('quantity', orderProductsVariation.quantity.toString());
                        const cartUrl = `https://www.aliexpress.com/p/trade/confirm.html?${searchParams.toString()}`
                        postMessageToLeboncoinExt(window, {
                          action: 'fill-cart-address',
                          aliexpressCartUrl: cartUrl,
                          address: addressForm,
                          orderItems: orderData.orderProductsVariations.map((orderProductsVariation) => ({
                            name: getTranslatedValue(orderProductsVariation.adVariation?.ad.title as unknown as Translations, translationLang) + ' - ' + orderProductsVariation.adVariation?.variationTypeValues.map((vtv) => getTranslatedValue(vtv.value as unknown as Translations, translationLang)).join(', '),
                            quantity: orderProductsVariation.quantity,
                          })),
                        })
                      }}
                    >
                      <FaShoppingCart className="mr-2" size={20} />
                      Open cart and fill address
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )
    })}

    {/* Display missing line items */}
    {/*missingLineItems.length > 0 && missingLineItems.map((lineItem, index) => (
      <React.Fragment key={lineItem.lineItemId}>
        <div className="flex items-center justify-start gap-2 mb-2 mt-2">
          <h3 className="text-lg font-medium text-gray-900">
            Line item #{lineItem.lineItemId}
          </h3>
          <Button
            preset="primary"
            size="sm"
            onClick={() => openModalWithLineItem(lineItem)}
          >
            <FaPlus className="mr-2" />
            Match this line item
          </Button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-md text-red-700 mb-2">
            The following line items from the original order has not been added to the order:
          </p>
          <div className="flex-1">
            <div className="font-medium text-gray-900">{lineItem.name}</div>
            <div className="text-md text-gray-500">
              Line Item ID: {lineItem.lineItemId} • Quantity: {lineItem.quantity} • 
              Price: {lineItem.pricePaidByCustomer ? formatCurrency(lineItem.pricePaidByCustomer) : 'N/A'}
            </div>
          </div>
        </div>
      </React.Fragment>
    ))*/}

    {/* Add Line Item Modal */}
    {showAddLineItemModal && (
      <AddLineItemModal
        orderId={orderData.id}
        destinationCountry={deliveryDestination as DeliveryDestination}
        onClose={handleCloseModal}
        // onSuccess={handleModalSuccess}
        isDirectSale={(orderData as any).shop.shopType === 'direct-sale'}
      />
    )}
  </>);
}


function TypeValueOption({
  typeValue,
  isSelected,
  selectCallback,
  lang,
}: {
  typeValue: any,
  isSelected: boolean,
  selectCallback?: () => void,
  lang?: string,
}) {
  const value = getTranslatedValue(typeValue.value as Translations, lang);

  return (
    <div
      className={cx(
        'flex flex-col',
        !isSelected && 'px-2 py-2 hover:bg-blue-100 cursor-pointer',
      )}
      onClick={selectCallback || undefined}
    >
      {/* {isOriginalTypeValue && !isSelected && (
        <div className="flex flex-row items-center mb-1">
          <FaCheckCircle className="mr-2" size={16} />
          <span className="text-sm text-gray-500">Original value</span>
        </div>
      )} */}
      <div className="flex flex-row items-center">
        {typeValue.medias?.[0] && (
          <img 
            src={typeValue.medias[0].url} 
            alt={value}
            className="w-8 h-8 rounded-md object-cover mr-2"
          />
        )}
        <span>{value}</span>
      </div>
    </div>
  )
}

export const SUPPLIER_PRODUCT_VARIATION_FRAGMENT = gql`
  fragment OrderPage_SupplierProductVariation on SupplierProductVariation {
    sku
    supplierProductId
    supplierName
    data

    quantityOrdered
    availableStock {
      destinationCountry
      availableStock
    }
    calculatedCost {
      destinationCountry
      totalCostIncludingVAT
      totalCostExcludingVAT
      totalCostVAT
      VATRate
      currency
    }
    lastSyncAt

    supplierProduct {
      id
      name
      supplierName
      data
      aliexpressQuickData {
        productId
      }
      externalProductId
      note
      originCountry
    }

    typeValues {
      id
      type {
        id
        type
      }
      value
      extraData
    }

    restockShipmentProductVariations {
      id
      quantity
      availableStock
      cost {
        unitCost
        totalCost
        totalFreightCost
        totalProductCost
      }
      restockShipmentProduct {
        id
        restockShipmentId
        externalOrderId
        sellerCompany
        shippingTrackingNumber
      }
    }
  }
`;

export const UPDATE_ORDER_PRODUCTS_VARIATION = gql`
  mutation UpdateOrderProductsVariation($id: ID!, $input: UpdateOrderProductsVariationInput!) {
    updateOrderProductsVariation(id: $id, input: $input) {
      id
      variationId
      supplierProductVariation {
        ...OrderPage_SupplierProductVariation @unmask
      }
    }
  }
  ${SUPPLIER_PRODUCT_VARIATION_FRAGMENT}
`;

export const DELETE_ORDER_PRODUCTS_VARIATION = gql`
  mutation DeleteOrderProductsVariation($id: ID!) {
    deleteOrderProductsVariation(id: $id)
  }
`;

const ORDER_PAGE_LINE_ITEMS_SYNC_ALIEXPRESS_SUPPLIER_PRODUCT = gql`
  mutation OrderPageLineItems_SyncAliexpressSupplierProduct(
    $productId: String!,
    $countries: [String!],
    $options: SyncAliexpressSupplierProductOptionsInput
  ) {
    syncAliexpressSupplierProduct(productId: $productId, countries: $countries, options: $options) {
      variations {
        ...OrderPage_SupplierProductVariation
      }
    }
  }
  ${SUPPLIER_PRODUCT_VARIATION_FRAGMENT}
`;